/**
 * Lenient CP onboarding form validation (mirrors utils/cpOnboardingFormValidation.js).
 */

const REG_NUMBER_MAX = 50;
const REG_ALLOWED = /^[A-Za-z0-9][A-Za-z0-9./\-\s]*$/;

export type ValidationResult = { valid: true } | { valid: false; error: string };

function normalizeRegNumber(value: string): string {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

export function validateRegistrationNumber(
  regNumber: string,
  entityType = 'company',
): ValidationResult {
  const raw = normalizeRegNumber(regNumber);
  const compact = raw.replace(/\s/g, '');
  const et = String(entityType || 'company').toLowerCase().trim();

  if (!raw) return { valid: false, error: 'Registration number is required.' };
  if (compact.length > REG_NUMBER_MAX) {
    return { valid: false, error: `Registration number is too long (max ${REG_NUMBER_MAX} characters).` };
  }
  if (!REG_ALLOWED.test(raw)) {
    return {
      valid: false,
      error: 'Use only letters, numbers, slashes, dots, or hyphens — as printed on your registration document.',
    };
  }
  if (!/\d/.test(compact)) {
    return {
      valid: false,
      error: 'Registration number should include at least one digit (not a name or free text).',
    };
  }
  if (et === 'company' && compact.length < 6) {
    return {
      valid: false,
      error: 'Company registration looks too short. Enter the number on your registration document.',
    };
  }
  if (et === 'sole_proprietor' && /^\d+$/.test(compact) && compact.length !== 13) {
    return {
      valid: false,
      error: 'If you are using a South African ID number, enter all 13 digits.',
    };
  }
  if (compact.length < 4) {
    return { valid: false, error: 'Registration number looks too short.' };
  }
  return { valid: true };
}

export function registrationFieldMeta(entityType = 'company') {
  const et = String(entityType || 'company').toLowerCase().trim();
  if (et === 'sole_proprietor') {
    return {
      label: 'Registration or ID number *',
      helperText: 'Registered business number from CIPC, or your 13-digit SA ID if you trade as an individual.',
      placeholder: 'e.g. 2024/123456/07 or 8001015009087',
    };
  }
  if (et === 'trust') {
    return {
      label: 'Trust registration number *',
      helperText: "Trust number as on your trust deed or Master's Office registration.",
      placeholder: 'e.g. IT1234/2015',
    };
  }
  return {
    label: 'Company registration number *',
    helperText:
      "The number on your company registration document. A South African company uses its CIPC number (e.g. 2024/123456/07). A company incorporated elsewhere uses that country's registration number.",
    placeholder: 'e.g. 2024/123456/07 or a foreign registration number',
  };
}

export const SIGNATORY_NAME_META = {
  label: 'Authorised signatory — full name *',
  helperText:
    'Full legal name of the person authorised to sign banking and contract documents for your organisation (as on your ID or resolution).',
  placeholder: 'e.g. Jane Doe',
};

export const SIGNATORY_ID_META = {
  label: 'Signatory SA ID or passport number *',
  helperText: '13-digit South African ID or passport number for the signatory named above.',
  placeholder: 'e.g. 8001015009087',
};

export const BANK_ACCOUNT_META = {
  label: 'Bank account number (optional)',
  helperText:
    'Your business bank account if you have one now. You can also provide this via a bank confirmation letter in the next step. Only the last 4 digits are shown after you leave the field.',
  placeholder: 'e.g. 1234567890',
};

export const BANK_ACCOUNT_HOLDER_META = {
  label: 'Bank account holder (optional)',
  helperText: 'Name on the bank account, as it appears on your bank confirmation letter.',
  placeholder: 'e.g. Sample Retailer (Pty) Ltd',
};

export const BANK_NAME_META = {
  label: 'Bank (optional)',
  helperText: 'The bank where this account is held.',
  placeholder: 'e.g. Your bank',
};

export const BANK_BRANCH_CODE_META = {
  label: 'Branch code (optional)',
  helperText: 'Branch or universal branch code (digits only).',
  placeholder: 'e.g. 051001',
};

export const VAT_NUMBER_META = {
  label: 'VAT registration number',
  helperText: 'Your SARS VAT number — shown on the monthly tax invoices MyMoolah issues on your behalf.',
  placeholder: 'e.g. 4123456789',
};

export function validateBranchCode(value: string): ValidationResult {
  const raw = String(value || '').trim();
  if (!raw) return { valid: true }; // optional
  if (!/^\d{4,10}$/.test(raw)) {
    return { valid: false, error: 'Branch code must be 4–10 digits.' };
  }
  return { valid: true };
}

export function validateVatNumber(value: string): ValidationResult {
  const raw = String(value || '').replace(/\s+/g, '');
  if (!raw) return { valid: true }; // optional unless VAT vendor — enforced by the form
  if (!/^4\d{9}$/.test(raw)) {
    return { valid: false, error: 'SA VAT numbers are 10 digits starting with 4.' };
  }
  return { valid: true };
}
